import type {
  DragnetJsExceptionCategory,
  JsExceptionRecordDto,
  JsHttpRequestRecordDto,
  PublishingResponseDto,
} from '@zg-rentals/ts-dragnet-client';
import { MaxLengthThrottledBatchWriter } from '@zg-rentals/util';
import { getEnvironment } from '@zg-rentals/environment-utils';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { redactUserInfo } from './redact';

export type DragnetError = {
  error: Error;
  httpRequest?: JsHttpRequestRecordDto;
  category: DragnetJsExceptionCategory;
};

// 12 kB
const MAX_BODY_SIZE = 12288;
export const MAX_NAME_LENGTH = 200;

export function parseStacktrace(stack: string) {
  const stackRegex = /at .* \((.*):(\d+):(\d+)\)/;
  const match = stackRegex.exec(stack);
  if (match) {
    const fileName = match[1];
    const lineNumber = parseInt(match[2], 10);
    return {
      fileName,
      lineNumber,
    };
  }
  return { fileName: 'unknown', lineNumber: -1 };
}

const MAX_EXCEPTIONS_TO_SEND = 100;

export function parseMethodName(stack: string) {
  const functionNameRegex = /at (.*) \(/;
  const match = functionNameRegex.exec(stack);
  if (match) {
    return match[1];
  }
  return undefined;
}

const CONTEXT_PATTERNS = [
  /(\blistingAlias\s+)([a-zA-Z0-9_]+)/gi,
  /(\bconversationId\s+)([a-zA-Z0-9_]+)/gi,
  /(\baliases\s+)([a-zA-Z0-9_]+)/gi,
  /(\bzuid\s+)([a-zA-Z0-9_]+)/gi,
  /(\breferenceId\s+)([a-zA-Z0-9_]+)/gi,
  /(\bapiKey\s+)([a-zA-Z0-9_]+)/gi,
  /(\bcsrfIv\s+)([a-zA-Z0-9_]+)/gi,
  /(\bcsrfToken\s+)([a-zA-Z0-9_]+)/gi,
];

export function maskIdentifiers(message: string) {
  let maskedText = message;

  for (const pattern of CONTEXT_PATTERNS) {
    maskedText = maskedText.replace(pattern, '$1redacted');
  }

  return maskedText;
}

export function extractHttpStatus(error?: Error): number | null {
  if (!error || typeof error !== 'object' || !('responseStatus' in error)) {
    return null;
  }

  const responseStatus = (error as { responseStatus?: number | string }).responseStatus;
  const status =
    typeof responseStatus === 'number'
      ? responseStatus
      : typeof responseStatus === 'string'
        ? parseInt(responseStatus, 10)
        : NaN;

  return !isNaN(status) && status > 0 ? status : null;
}

export function normalizeHttpStatus(status: number): string {
  if (status === 401) return 'HTTP 401 Unauthorized';
  if (status === 403) return 'HTTP 403 Forbidden';
  if (status === 404) return 'HTTP 404 Not Found';
  if (status === 429) return 'HTTP 429 Rate Limited';
  if (status === 500) return 'HTTP 500 Internal Server Error';
  if (status === 502) return 'HTTP 502 Bad Gateway';
  if (status === 503) return 'HTTP 503 Service Unavailable';
  if (status === 504) return 'HTTP 504 Gateway Timeout';

  if (status >= 400 && status < 500) return `HTTP ${status} Client Error`;
  if (status >= 500) return `HTTP ${status} Server Error`;

  return `HTTP ${status}`;
}

export function getNameFromMessage(error: Error, httpRequest?: JsHttpRequestRecordDto): string {
  // First priority: check responseStatus on error object
  const status = extractHttpStatus(error);
  if (status !== null) {
    return normalizeHttpStatus(status);
  }

  // Second priority: extract status from httpRequest body (context from logError)
  if (httpRequest?.body) {
    try {
      const context = JSON.parse(httpRequest.body);
      if (context.status && typeof context.status === 'number' && context.status > 0) {
        return normalizeHttpStatus(context.status);
      }
    } catch {
      // Invalid JSON, continue to message normalization
    }
  }

  const message = error.message || '';

  // Normalize timeout errors to group them together
  if (/timeout|timed out/i.test(message)) {
    return 'Request timeout';
  }

  let name = message.replace(/\W/g, ' ').replace(/\s+/g, ' ').trim();

  if (name.toLowerCase().includes('failed to decode param')) {
    name = 'Failed to decode param';
  }

  // remove dynamic react query names. Info is in the context
  if (/api.*data is undefined/g.test(name)) {
    name = 'api data is undefined';
  }

  name = maskIdentifiers(name);

  return name.substring(0, MAX_NAME_LENGTH);
}

export abstract class Dragnet {
  private readonly sendException: MaxLengthThrottledBatchWriter<JsExceptionRecordDto, PublishingResponseDto>;

  protected abstract request(batch: Array<JsExceptionRecordDto>): Promise<PublishingResponseDto>;

  constructor(
    protected readonly appName: string,
    protected readonly buildVersion: number,
    protected readonly serverName: string,
    throttleTime = 5_000,
  ) {
    this.sendException = new MaxLengthThrottledBatchWriter(
      async (batch: Array<JsExceptionRecordDto>) => this.request(batch),
      throttleTime,
      MAX_EXCEPTIONS_TO_SEND,
      (reason) => getGlobalLogger('dragnetNode')?.error(reason),
    );
  }

  public reportError({ error, httpRequest, category }: DragnetError) {
    if (!error.stack) {
      return;
    }

    /* Sample:
      Error: Region is missing
      at default (/usr/src/app/node_modules/@smithy/config-resolver/dist-cjs/index.js:117:11)
      at /usr/src/app/node_modules/@smithy/node-config-provider/dist-cjs/index.js:90:104
      at /usr/src/app/node_modules/@smithy/property-provider/dist-cjs/index.js:97:33
    */

    const { fileName, lineNumber } = parseStacktrace(error.stack);

    let fullStackTrace = error.stack;
    if (error.message && !error.stack.includes(error.message)) {
      fullStackTrace = `${error.message}\n${error.stack}`;
    }

    const exceptionRecord: JsExceptionRecordDto = {
      created: Date.now(),
      exceptionLocation: fileName,
      name: getNameFromMessage(error, httpRequest),
      serverName: this.serverName,
      serviceName: this.appName,
      stackTrace: fullStackTrace,
      type: error.name,
      appVersion: `${this.buildVersion}`,
      callOrigin: fileName,
      lineNumber,
      methodName: parseMethodName(error.stack),
      environment: getEnvironment() ?? 'production',
      category,
      additionalAlertRecipients: [],
      httpRequest: httpRequest
        ? {
            ...httpRequest,
            body: httpRequest?.body ? redactUserInfo(httpRequest.body).substring(0, MAX_BODY_SIZE) : undefined,
          }
        : undefined,
    };

    const p = this.sendException.write(exceptionRecord);
    this.recordMetrics(exceptionRecord);
    return p;
  }

  private recordMetrics(exceptionRecord: JsExceptionRecordDto) {
    const monitor = getGlobalMonitor();
    monitor?.count({ name: 'Exception ' + exceptionRecord.category });
    monitor?.count({ name: 'Exception ' + `${exceptionRecord.category} ${this.appName}` });
    monitor?.count({ name: 'Exception ' + `name ${exceptionRecord.name}` });
    monitor?.count({ name: 'Exception ' + exceptionRecord.type });
  }
}
